import type {
  CollabPresencePeer,
  CollabStatus,
  CommentThread,
  MentionCandidate,
  SupabaseFileYjsProvider,
  WorkspacePresencePeer,
} from '@bangle.io/supabase';
import { atom } from 'jotai';
import type * as Y from 'yjs';

export const $collabPeers = atom<CollabPresencePeer[]>([]);
export const $collabStatus = atom<CollabStatus | null>(null);
export const $collabYdoc = atom<Y.Doc | null>(null);
export const $collabCommentThreads = atom<CommentThread[]>([]);

/** Sticky workspace-level peers (visible across routes while in a cloud workspace). */
export const $workspacePresencePeers = atom<WorkspacePresencePeer[]>([]);

/** Cloud workspace the local client is announcing presence for (survives settings). */
export const $stickyCloudWorkspace = atom<{
  workspaceId: string;
  wsName: string;
} | null>(null);

/** Cmd/Ctrl-click target: navigate to the peer's note then focus their caret. */
export const $pendingPresenceJump = atom<{
  userId: string;
  filePath: string;
} | null>(null);

export type ActiveCollabSession = {
  provider: SupabaseFileYjsProvider;
  ydoc: Y.Doc;
  workspaceId: string;
  filePath: string;
  mentionCandidates: MentionCandidate[];
  unsubPresence: () => void;
  unsubComments: () => void;
};

let activeSession: ActiveCollabSession | null = null;

export function getActiveCollabSession(): ActiveCollabSession | null {
  return activeSession;
}

function teardownSession(
  session: ActiveCollabSession,
  persist: boolean,
): void {
  session.unsubPresence();
  session.unsubComments();
  if (persist) {
    void session.provider.flushPersist().finally(() => {
      session.provider.destroy();
      session.ydoc.destroy();
    });
    return;
  }
  session.provider.destroy({ persist: false });
  session.ydoc.destroy();
}

/**
 * Replace the active collab session. By default the previous session is
 * flushed to PocketBase so navigations do not drop unsaved Yjs. Pass
 * `{ persistPrevious: false }` (or open the same path again) to abandon
 * without writing — required after delete / same-name recreate so teardown
 * cannot resurrect path-keyed ydoc that storage already purged.
 */
export function setActiveCollabSession(
  session: ActiveCollabSession | null,
  opts?: { persistPrevious?: boolean },
) {
  if (activeSession && activeSession !== session) {
    const previous = activeSession;
    activeSession = null;
    const samePath =
      session != null &&
      session.workspaceId === previous.workspaceId &&
      session.filePath === previous.filePath;
    const persist = opts?.persistPrevious !== false && !samePath;
    teardownSession(previous, persist);
  }
  activeSession = session;
}

/** Drop the active session without flushing remote or local ydoc. */
export function abandonActiveCollabSession() {
  setActiveCollabSession(null, { persistPrevious: false });
}

export function getMentionCandidates(): MentionCandidate[] {
  return activeSession?.mentionCandidates ?? [];
}
